import type { V6Client } from "@aws-amplify/api-graphql";
import type { ReteynSchema } from "../schema/index.js";
import {
  collect,
  parseTruthyResponse,
  pickRandom,
  toIterable,
} from "../dao/index.js";
import pMap from "p-map";
import chunk from "lodash/chunk.js";

export async function createMockSubmissions(
  client: V6Client<ReteynSchema>,
  reteynerId: string,
): Promise<void> {
  const emails = Array.from(
    new Array(20),
    () => window.crypto.randomUUID() + "@example.com",
  );
  const reteyner = await parseTruthyResponse(
    client.models.Reteyner.get({ id: reteynerId }),
  );
  const org = await parseTruthyResponse(reteyner.organisation({}));
  const options = { concurrency: 100 };
  const contacts = await pMap(
    emails,
    (email) =>
      parseTruthyResponse(
        client.models.Contact.create({ email, organisationId: org.id }),
      ),
    options,
  );
  for (const contactBatch of chunk(contacts, 50)) {
    const groupName = contactBatch[0].email.split("@")[0];
    const group = await parseTruthyResponse(
      client.models.Group.create({ name: groupName, organisationId: org.id }),
    );
    await parseTruthyResponse(
      client.models.GroupEnrollment.create({
        groupId: group.id,
        reteynerId: reteyner.id,
      }),
    );
    await Promise.all(
      contactBatch.map((contact) =>
        parseTruthyResponse(
          client.models.ContactGroup.create({
            contactId: contact.id,
            groupId: group.id,
          }),
        ),
      ),
    );
  }
  for await (const topic of toIterable((nextToken) =>
    reteyner.topics({ nextToken }),
  )) {
    for await (const question of toIterable((nextToken) =>
      topic.questions({ nextToken }),
    )) {
      const answers = await collect(
        toIterable((nextToken) => question.answers({ nextToken })),
      );
      const tests = await pMap(
        contacts,
        (contact) =>
          parseTruthyResponse(
            client.models.Test.create({
              questionId: question.id,
              contactId: contact.id,
              userRequested: true,
            }),
          ),
        options,
      );
      await pMap(
        tests,
        (test) =>
          parseTruthyResponse(
            client.models.Submission.create({
              testId: test.id,
              answerId: pickRandom(answers).id,
            }),
          ),
        options,
      );
    }
  }
}
